/**
 * Step o1x `reveal` (HA-252) — fenêtre de révélation UNIQUE d'un secret (clé API, secret de
 * signature webhook). Remplace l'ancien `msg` éphémère (toast de 60 s, et repli `console.log`
 * quand aucune notification n'était montée).
 *
 *   { t: "reveal", title, warning, label, secret, close? }
 *
 * Contrat :
 *  - `role="dialog"` + `aria-modal`, titre et avertissement liés (`aria-labelledby`/`describedby`) ;
 *  - montée sur `document.body`, HORS de la racine de l'app : le `reloadApp` qui précède dans les
 *    mêmes `steps` ne l'efface pas ;
 *  - focus sur le secret (sélectionné), piégé dans la fenêtre ;
 *  - « Copier » : presse-papiers, repli sélection + `execCommand`. Un échec est ANNONCÉ et la
 *    fenêtre reste ouverte — jamais de confirmation mensongère ;
 *  - fermeture EXPLICITE seulement (bouton) : Échap et clic hors fenêtre ne ferment pas, ils le disent ;
 *  - à la fermeture, valeur effacée, nœud retiré du DOM, aucune référence conservée ; focus rendu
 *    au déclencheur s'il existe encore, sinon au document ;
 *  - le secret n'est JAMAIS journalisé (aucun `log`/`console`), ni stocké (état, historique, cache).
 *    Confidentialité DOM/état applicatif seulement : les outils réseau du navigateur ne sont pas couverts.
 */

const COPIED = "Secret copié dans le presse-papiers.";
const COPY_FAILED = "Copie impossible — sélectionnez le secret et copiez-le manuellement.";
const CLOSE_HINT = "Pour fermer, utilisez le bouton « J'ai copié le secret, fermer ».";

let sequence = 0;

/** Ouvre la fenêtre ; rend la fonction de fermeture (tests). Ne lève jamais vers l'appelant. */
export function openSecretReveal(data: Map<any, any>): (() => void) | null {
    try {
        return mount(data);
    } catch {
        // Ne JAMAIS retomber sur msg/log : le secret ne doit pas fuiter par un chemin d'erreur.
        return null;
    }
}

function mount(data: Map<any, any>): () => void {
    const secret = String(data.get("secret") ?? "");
    const id = `ha-reveal-${++sequence}`;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const backdrop = el("div", {
        "data-ha-reveal": "",
        style: "position:fixed;inset:0;z-index:10000;background:rgba(10,20,30,.55);display:flex;align-items:center;justify-content:center;padding:16px",
    });
    const dialog = el("div", {
        role: "dialog",
        "aria-modal": "true",
        "aria-labelledby": `${id}-title`,
        "aria-describedby": `${id}-warning`,
        class: "ha-reveal",
        style: "background:#fff;color:#1d2733;border-radius:12px;max-width:560px;width:100%;padding:20px;box-shadow:0 12px 40px rgba(0,0,0,.25)",
    });
    const title = el("h2", { id: `${id}-title`, style: "margin:0 0 8px;font-size:18px" }, String(data.get("title") ?? "Secret à copier"));
    const warning = el("p", { id: `${id}-warning`, style: "margin:0 0 12px" },
        String(data.get("warning") ?? "Ce secret ne sera plus jamais affiché. Copiez-le maintenant."));
    const label = el("label", { for: `${id}-secret`, style: "display:block;font-weight:600;margin-bottom:4px" }, String(data.get("label") ?? "Secret"));
    const input = el("input", {
        id: `${id}-secret`, type: "text", readonly: "", autocomplete: "off", spellcheck: "false",
        style: "width:100%;box-sizing:border-box;font-family:monospace;padding:8px;border:1px solid #9aa7b4;border-radius:6px",
    }) as HTMLInputElement;
    input.value = secret; // propriété, pas attribut : jamais sérialisé dans le DOM
    const status = el("p", { "aria-live": "polite", role: "status", style: "min-height:1.4em;margin:8px 0" });
    const copy = el("button", { type: "button", class: "btn btn--outline" }, "Copier") as HTMLButtonElement;
    const close = el("button", { type: "button", class: "btn btn--primary" },
        String(data.get("close") ?? "J'ai copié le secret, fermer")) as HTMLButtonElement;
    const actions = el("div", { style: "display:flex;gap:8px;justify-content:flex-end;flex-wrap:wrap" });
    actions.append(copy, close);
    dialog.append(title, warning, label, input, status, actions);
    backdrop.append(dialog);
    document.body.appendChild(backdrop);

    let closed = false;
    const focusables = (): HTMLElement[] => [input, copy, close];

    const onKey = (e: KeyboardEvent): void => {
        if (e.key === "Escape") {
            e.preventDefault();
            status.textContent = CLOSE_HINT;
            return;
        }
        if (e.key === "Tab") {
            const list = focusables();
            const index = list.indexOf(document.activeElement as HTMLElement);
            const next = e.shiftKey ? (index <= 0 ? list.length - 1 : index - 1) : (index + 1) % list.length;
            e.preventDefault();
            list[next].focus();
        }
    };
    const onBackdrop = (e: MouseEvent): void => {
        if (e.target === backdrop) {
            status.textContent = CLOSE_HINT;
        }
    };

    copy.addEventListener("click", async () => {
        const copied = await copySecret(input, () => !closed);
        if (closed) { return; } // fermée pendant la copie : aucune continuation (revue 14449)
        status.textContent = copied ? COPIED : COPY_FAILED;
    });

    const dispose = (): void => {
        if (closed) { return; }
        closed = true;
        input.value = "";
        backdrop.removeEventListener("keydown", onKey, true);
        backdrop.removeEventListener("click", onBackdrop);
        backdrop.remove();
        const target = opener && opener.isConnected ? opener : null;
        if (target) {
            target.focus();
        } else {
            (document.body as HTMLElement).focus?.();
        }
    };
    close.addEventListener("click", dispose);
    backdrop.addEventListener("keydown", onKey, true);
    backdrop.addEventListener("click", onBackdrop);

    input.focus();
    input.select();
    return dispose;
}

/**
 * Presse-papiers, puis repli sélection + execCommand ; `true` seulement si la copie est confirmée.
 * `open()` est relu APRÈS l'attente asynchrone : une fenêtre fermée entre-temps n'exécute plus
 * aucun repli (le champ est vidé et détaché — revue 14449).
 */
async function copySecret(input: HTMLInputElement, open: () => boolean): Promise<boolean> {
    const value = input.value;
    try {
        if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
            await navigator.clipboard.writeText(value);
            return open();
        }
    } catch {
        // permission refusée ou contexte non sécurisé : repli ci-dessous, si la fenêtre est encore là
    }
    if (!open()) {
        return false;
    }
    try {
        input.focus();
        input.select();
        return typeof document.execCommand === "function" && document.execCommand("copy") === true;
    } catch {
        return false;
    }
}

function el(tag: string, attrs: Record<string, string>, text?: string): HTMLElement {
    const node = document.createElement(tag);
    for (const [name, value] of Object.entries(attrs)) {
        node.setAttribute(name, value);
    }
    if (text !== undefined) {
        node.textContent = text; // texte, jamais HTML
    }
    return node;
}
